import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiSettings } from 'react-icons/fi';
import { listarProcessos, excluirProcesso } from '../../services/processos';
import { carregarCatalogo } from '../../services/processoTipos';
import { labelTipo, labelStatus, nomeEtapa, totalEtapas, STATUS_PROCESSO } from './catalogoUtils';
import ProcessoFormModal from './ProcessoFormModal';
import ConfirmarModal from './ConfirmarModal';
import styles from './ProcessosLista.module.css';

const DIA_MS = 24 * 60 * 60 * 1000;

function diasParado(dataEntradaEtapa) {
  if (!dataEntradaEtapa) return 0;
  const diff = Date.now() - new Date(dataEntradaEtapa).getTime();
  return Math.max(0, Math.floor(diff / DIA_MS));
}

function ProcessosLista({ onAbrirProcesso, onAbrirConfig }) {
  const [processos, setProcessos]   = useState([]);
  const [catalogo, setCatalogo]     = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');
  const [busca, setBusca]           = useState('');
  const [filtroTipo, setFiltroTipo]             = useState('');
  const [filtroStatus, setFiltroStatus]         = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [mostrarNovo, setMostrarNovo] = useState(false);
  const [editando, setEditando]     = useState(null);
  const [excluindo, setExcluindo]   = useState(null);
  const [apagando, setApagando]     = useState(false);
  const [erroExcluir, setErroExcluir] = useState('');

  const carregar = useCallback(() => {
    setCarregando(true); setErro('');
    Promise.all([listarProcessos(), carregarCatalogo()])
      .then(([r, cat]) => { setProcessos(r.items || []); setCatalogo(cat); })
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const responsaveis = useMemo(() => (
    [...new Set(processos.map(p => p.responsavel).filter(Boolean))].sort()
  ), [processos]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return processos
      .filter(p => !filtroTipo       || p.tipo === filtroTipo)
      .filter(p => !filtroStatus     || p.status === filtroStatus)
      .filter(p => !filtroResponsavel || p.responsavel === filtroResponsavel)
      .filter(p => !q || (p.cliente_nome || '').toLowerCase().includes(q) || (p.cliente_codigo || '').toLowerCase().includes(q))
      .sort((a, b) => diasParado(b.data_entrada_etapa) - diasParado(a.data_entrada_etapa));
  }, [processos, filtroTipo, filtroStatus, filtroResponsavel, busca]);

  function aoCriar(novo) {
    setProcessos(prev => [novo, ...prev]);
    setMostrarNovo(false);
    onAbrirProcesso(novo.id);
  }

  function aoSalvarEdicao(atualizado) {
    setProcessos(prev => prev.map(p => p.id === atualizado.id ? atualizado : p));
    setEditando(null);
  }

  async function confirmarExclusao() {
    setApagando(true); setErroExcluir('');
    try {
      await excluirProcesso(excluindo.id);
      setProcessos(prev => prev.filter(p => p.id !== excluindo.id));
      setExcluindo(null);
    } catch (e) {
      setErroExcluir(e.message);
    } finally {
      setApagando(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.titulo}>Processos</h1>
        <div className={styles.pageHeaderAcoes}>
          <button className={styles.btnConfigurar} onClick={onAbrirConfig} type="button">
            <FiSettings size={14} /> Configurar etapas
          </button>
          <button className={styles.btnNovo} onClick={() => setMostrarNovo(true)} type="button">
            <FiPlus size={15} /> Novo processo
          </button>
        </div>
      </div>

      <div className={styles.filtrosRow}>
        <div className={styles.buscaWrapper}>
          <FiSearch className={styles.buscaIcon} size={15} />
          <input
            className={styles.buscaInput}
            placeholder="Buscar por cliente..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>

        <select className={styles.filtroSelect} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          {catalogo.map(t => <option key={t.chave} value={t.chave}>{t.nome}</option>)}
        </select>

        <select className={styles.filtroSelect} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos os status</option>
          {STATUS_PROCESSO.map(s => <option key={s.valor} value={s.valor}>{s.label}</option>)}
        </select>

        {responsaveis.length > 0 && (
          <select className={styles.filtroSelect} value={filtroResponsavel} onChange={e => setFiltroResponsavel(e.target.value)}>
            <option value="">Todos os responsáveis</option>
            {responsaveis.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
      </div>

      {erro && <div className={styles.erroFaixa}>{erro}</div>}

      {carregando ? (
        <p className={styles.vazio}>Carregando...</p>
      ) : (
        <div className={styles.tabela}>
          <div className={styles.tabelaHeader}>
            <div>Cliente</div>
            <div>Tipo</div>
            <div>Etapa atual</div>
            <div>Responsável</div>
            <div>Dias parado</div>
            <div>Pendência</div>
            <div>Status</div>
            <div>Ações</div>
          </div>

          {filtrados.length === 0 ? (
            <div className={styles.tabelaVazia}>
              <p>Nenhum processo encontrado.</p>
            </div>
          ) : (
            filtrados.map(p => {
              const dias = diasParado(p.data_entrada_etapa);
              return (
                <div
                  key={p.id}
                  className={`${styles.tabelaRow} ${dias > 5 ? styles.rowAtrasada : ''}`}
                  onClick={() => onAbrirProcesso(p.id)}
                >
                  <div className={styles.colCliente}>
                    <span className={styles.clienteNome}>{p.cliente_nome || '—'}</span>
                    {p.cliente_codigo && <span className={styles.clienteCodigo}>{p.cliente_codigo}</span>}
                  </div>
                  <div>{labelTipo(catalogo, p.tipo)}</div>
                  <div className={styles.colEtapa}>
                    <span>{nomeEtapa(catalogo, p.tipo, p.etapa_atual)}</span>
                    <span className={styles.etapaContagem}>{p.etapa_atual} de {totalEtapas(catalogo, p.tipo) || '?'}</span>
                  </div>
                  <div>{p.responsavel || '—'}</div>
                  <div className={dias > 5 ? styles.diasAtrasado : ''}>{dias} dia{dias === 1 ? '' : 's'}</div>
                  <div className={styles.colPendencia}>{p.pendencia || '—'}</div>
                  <div>
                    <span className={`${styles.statusBadge} ${styles['status_' + p.status]}`}>{labelStatus(p.status)}</span>
                  </div>
                  <div className={styles.colAcoes}>
                    <button
                      type="button"
                      className={styles.acaoBtn}
                      title="Editar"
                      onClick={e => { e.stopPropagation(); setEditando(p); }}
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      type="button"
                      className={styles.acaoBtn}
                      title="Excluir"
                      onClick={e => { e.stopPropagation(); setErroExcluir(''); setExcluindo(p); }}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {mostrarNovo && (
        <ProcessoFormModal catalogo={catalogo} onFechar={() => setMostrarNovo(false)} onCriado={aoCriar} />
      )}

      {editando && (
        <ProcessoFormModal catalogo={catalogo} processo={editando} onFechar={() => setEditando(null)} onSalvo={aoSalvarEdicao} />
      )}

      {excluindo && (
        <ConfirmarModal
          titulo="Excluir processo"
          mensagem={`Excluir o processo de ${labelTipo(catalogo, excluindo.tipo)} de ${excluindo.cliente_nome || 'este cliente'}? O histórico de etapas será apagado junto. Esta ação não pode ser desfeita.`}
          textoConfirmar="Excluir"
          perigo
          confirmando={apagando}
          erro={erroExcluir}
          onConfirmar={confirmarExclusao}
          onCancelar={() => setExcluindo(null)}
        />
      )}
    </div>
  );
}

export default ProcessosLista;
